import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { t } from "../locale";
import {
  fetchBookingPage,
  type BookingPageData,
  type AvailableSlot,
} from "../../lib/slottingApi";
import { ApiError } from "../../lib/api";
import { formatDateOnly, dateKeyOf } from "../lib/offering-date";
import { slottingErrorText } from "../lib/slotting-error";
import { FormModal } from "./formModal";
import { reportValidationError, shouldValidateField } from "./formInput";
import { extractWallParts, localTimeToIso } from "../../lib/utils/datetime-tz";
import {
  WB_BOOKING_CAPACITY_EVENT,
  WB_BOOKING_SUMMARY_EVENT,
  WB_BOOKING_ERROR_EVENT,
  WB_BOOKING_REGISTER_EVENT,
  WB_VALIDATE_EVENT,
  WB_FIELD_CHANGED_EVENT,
  dispatchCachedEvent,
  evaluateVisibilityFilter,
  resolveFieldSpacing,
  type ParentFilter,
} from "./shared";
import { useWBTheme, useEventTimezone } from "../theme/WBThemeRuntimeProvider";

function parseBorderRadiusPxLocal(tw: string): number {
  const map: Record<string, number> = {
    "rounded-none": 0, "rounded-sm": 2, "rounded": 4, "rounded-md": 6,
    "rounded-lg": 8, "rounded-xl": 12, "rounded-2xl": 16, "rounded-3xl": 24,
  };
  return map[tw] ?? 8;
}

function tailwindFontSizeToCssLocal(tw: string): string {
  const map: Record<string, string> = {
    "text-xs": "0.75rem", "text-sm": "0.875rem", "text-base": "1rem",
    "text-lg": "1.125rem", "text-xl": "1.25rem",
  };
  return map[tw] ?? "1rem";
}

function parsePaddingPx(tw?: string): string | undefined {
  if (!tw) return undefined;
  const map: Record<string, string> = {
    "p-0": "0", "p-1": "4px", "p-2": "8px", "p-3": "12px",
    "p-4": "16px", "p-5": "20px", "p-6": "24px", "p-8": "32px",
  };
  return map[tw];
}

/* ---------- Timezone & time helpers for REQUEST_BASED constrained selectors ---------- */

function parseHHMM(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function formatMinutesHHMM(mins: number): string {
  const h = Math.floor(Math.min(mins, 1440) / 60);
  const m = Math.min(mins, 1440) % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function generateTimeOptions(fromMin: number, toMin: number): string[] {
  const opts: string[] = [];
  if (fromMin > toMin) return opts;
  const start = Math.max(0, Math.ceil(fromMin / 15) * 15);
  for (let m = start; m <= Math.min(1440, toMin); m += 15) {
    opts.push(formatMinutesHHMM(m));
  }
  return opts;
}

/**
 * Adapters around the shared datetime-tz helpers, preserving the
 * `{ date, time }` shape that bookingGroup's call sites expect. New code in
 * the repo should prefer `extractWallParts` / `wallPartsToIso` /
 * `localTimeToIso` directly.
 */
function utcToTzParts(utcIso: string, tz: string): { date: string; time: string } {
  const p = extractWallParts(utcIso, tz);
  return {
    date: `${p.year}-${String(p.month + 1).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`,
    time: `${String(p.hour).padStart(2, "0")}:${String(p.minute).padStart(2, "0")}`,
  };
}

function tzToUtcIso(dateStr: string, timeStr: string, tz: string): string {
  return localTimeToIso(dateStr, timeStr, tz);
}

export type BookingSelection = {
  treatmentId: string;
  laneId: string;
  startDt: string;
  endDt: string;
  companions: string[];
};

/**
 * BookingGroup — booking step rendered inside DataInput.
 * Shows a compact summary inline with hidden form fields.
 * The full booking flow (treatment/date/time selection) opens in a modal.
 */
export function BookingGroup({
  offeringId,
  bookingMode,
  offeringName,
  treatmentsLabel,
  availabilityLabel,
  selectDateLabel,
  availableTimesLabel,
  addWindowLabel,
  treatmentRequired,
  isVisible,
  groupLabel,
  groupLabelColor,
  backgroundColor,
  padding,
  border,
  borderColor,
  borderWidth,
  borderStyle,
  borderSides,
  borderRadius,
  fieldSpacing,
  modalBackgroundColor,
  modalBorderRadius,
  modalMaxWidth,
  modalTitleColor,
  modalTitleFontSize,
  modalFooterBorderColor,
  modalBackdropOpacity,
  cancelButtonLabel,
  cancelButtonBorderColor,
  cancelButtonTextColor,
  cancelButtonBackgroundColor,
  cancelButtonBorderRadius,
  confirmButtonLabel,
  confirmButtonBackgroundColor,
  confirmButtonTextColor,
  confirmButtonBorderRadius,
  summaryCardBorderColor,
  summaryCardBorderRadius,
  selectionBorderColor,
  selectionActiveBorderColor,
  selectionActiveBackgroundColor,
  editButtonLabel,
  editButtonColor,
  closeIconColor,
  modalBorderColor,
  cancelButtonBgHoverColor,
  cancelButtonTextHoverColor,
  cancelButtonBorderHoverColor,
  confirmButtonBgHoverColor,
  confirmButtonTextHoverColor,
  availabilityInputBorderColor,
  availabilityInputBackgroundColor,
  availabilityInputTextColor,
  availabilityInputBorderRadius,
  treatmentFilters,
  laneFilters,
}: {
  offeringId?: string;
  bookingMode?: "REQUEST_BASED" | "AUTO_BOOKABLE";
  offeringName?: string;
  treatmentsLabel?: string;
  availabilityLabel?: string;
  selectDateLabel?: string;
  availableTimesLabel?: string;
  addWindowLabel?: string;
  treatmentRequired?: boolean;
  isVisible?: boolean;
  groupLabel?: string;
  groupLabelColor?: string;
  backgroundColor?: string;
  padding?: string;
  border?: boolean;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderSides?: string[];
  borderRadius?: string;
  fieldSpacing?: string;
  modalBackgroundColor?: string;
  modalBorderRadius?: string;
  modalMaxWidth?: string;
  modalTitleColor?: string;
  modalTitleFontSize?: string;
  modalFooterBorderColor?: string;
  modalBackdropOpacity?: number;
  cancelButtonLabel?: string;
  cancelButtonBorderColor?: string;
  cancelButtonTextColor?: string;
  cancelButtonBackgroundColor?: string;
  cancelButtonBorderRadius?: string;
  confirmButtonLabel?: string;
  confirmButtonBackgroundColor?: string;
  confirmButtonTextColor?: string;
  confirmButtonBorderRadius?: string;
  summaryCardBorderColor?: string;
  summaryCardBorderRadius?: string;
  selectionBorderColor?: string;
  selectionActiveBorderColor?: string;
  selectionActiveBackgroundColor?: string;
  editButtonLabel?: string;
  editButtonColor?: string;
  closeIconColor?: string;
  modalBorderColor?: string;
  cancelButtonBgHoverColor?: string;
  cancelButtonTextHoverColor?: string;
  cancelButtonBorderHoverColor?: string;
  confirmButtonBgHoverColor?: string;
  confirmButtonTextHoverColor?: string;
  availabilityInputBorderColor?: string;
  availabilityInputBackgroundColor?: string;
  availabilityInputTextColor?: string;
  availabilityInputBorderRadius?: string;
  treatmentFilters?: { treatmentId: string; treatmentName: string; filter: ParentFilter }[];
  laneFilters?: { laneId: string; laneName: string; filter: ParentFilter }[];
}) {
  const [data, setData] = useState<BookingPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apiStatus, setApiStatus] = useState<"ok" | "access_denied" | "error" | null>(null);
  const [selectedTreatment, setSelectedTreatment] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [selectedTreatmentIds, setSelectedTreatmentIds] = useState<Set<string>>(new Set());
  const [availabilityWindows, setAvailabilityWindows] = useState<{ date: string; startTime: string; endTime: string }[]>([{ date: "", startTime: "", endTime: "" }]);
  const [modalOpen, setModalOpen] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);
  const [confirmHovered, setConfirmHovered] = useState(false);
  const [windowErrors, setWindowErrors] = useState<(string | null)[]>([]);
  const [treatmentError, setTreatmentError] = useState<string | null>(null);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [modalValidationError, setModalValidationError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track live field values for filter evaluation (mirrors useLinkVisibilityUser in appBar)
  const { session } = useWBTheme();
  // Re-audit 2026-09-23 (L-3): the fallback zone when the offering carries
  // none is the EVENT's, as in slottingBooking/slottingAvailability — never
  // the visitor's browser, which put two visitors on different calendar days.
  const eventTz = useEventTimezone();
  const [liveFieldValues, setLiveFieldValues] = useState<Record<string, string>>({});
  useEffect(() => {
    const STATUS_ALIASES = ["state", "acceptanceState", "status"];
    function onFieldChanged(e: Event) {
      const { field, value } = (e as CustomEvent).detail as { field: string; value: string };
      setLiveFieldValues((prev) => {
        const next = { ...prev, [field]: value };
        if (STATUS_ALIASES.includes(field)) {
          for (const alias of STATUS_ALIASES) next[alias] = value;
        }
        return next;
      });
    }
    window.addEventListener(WB_FIELD_CHANGED_EVENT, onFieldChanged);
    return () => window.removeEventListener(WB_FIELD_CHANGED_EVENT, onFieldChanged);
  }, []);
  const filterUser = useMemo<Record<string, unknown>>(
    () => ({ ...(session?.user ?? {}), ...liveFieldValues }),
    [session, liveFieldValues],
  );

  // Build sets of allowed treatment/lane IDs based on filter evaluation
  const allowedTreatmentIds = useMemo(() => {
    if (!treatmentFilters?.length || !data) return null; // null = no filtering
    const allowed = new Set<string>();
    for (const rule of treatmentFilters) {
      if (evaluateVisibilityFilter(rule.filter, filterUser)) {
        allowed.add(rule.treatmentId);
      }
    }
    // Treatments without a filter rule are always visible
    const filteredIds = new Set(treatmentFilters.map((r) => r.treatmentId));
    for (const tr of data.treatments) {
      if (!filteredIds.has(tr.treatmentId)) allowed.add(tr.treatmentId);
    }
    return allowed;
  }, [treatmentFilters, data, filterUser]);

  const allowedLaneIds = useMemo(() => {
    if (!laneFilters?.length || !data) return null; // null = no filtering
    const allowed = new Set<string>();
    for (const rule of laneFilters) {
      if (evaluateVisibilityFilter(rule.filter, filterUser)) {
        allowed.add(rule.laneId);
      }
    }
    // Lanes without a filter rule are always visible
    const filteredIds = new Set(laneFilters.map((r) => r.laneId));
    for (const lane of data.lanes) {
      if (!filteredIds.has(lane.laneId)) allowed.add(lane.laneId);
    }
    return allowed;
  }, [laneFilters, data, filterUser]);

  useEffect(() => {
    let active = true;
    if (!offeringId) {
      setLoading(false);
      return;
    }
    fetchBookingPage(offeringId)
      .then((d) => {
        if (!active) return;
        setData(d);
        setApiStatus("ok");
        // Treatment is always explicitly selected by the user — no auto-select
      })
      .catch((err) => {
        if (!active) return;
        if (err instanceof ApiError && err.status === 403) {
          setApiStatus("access_denied");
          setError(t("bookingAccessDenied"));
        } else {
          setApiStatus("error");
          // Fix round 1 (l8 W-1): same lane, same mapping as the standalone
          // slotting widgets — 401 ⇒ login sentence, 503 ⇒ outage, else the
          // Worker's own message; a network failure reads localised too.
          setError(slottingErrorText(err, "slottingFailedToLoad"));
        }
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => { active = false; };
  }, [offeringId]);

  // Register this BookingGroup so consumers (OverviewGroup, CompanionsGroup) can discover it
  useEffect(() => {
    if (!offeringId) return;
    dispatchCachedEvent(`${WB_BOOKING_REGISTER_EVENT}:${offeringId}`, { offeringId });
    window.dispatchEvent(new CustomEvent(WB_BOOKING_REGISTER_EVENT, { detail: { offeringId } }));
    return () => {
      // Unregister on unmount: clear cached registration, summary, and capacity
      dispatchCachedEvent(`${WB_BOOKING_REGISTER_EVENT}:${offeringId}`, null);
      dispatchCachedEvent(`${WB_BOOKING_SUMMARY_EVENT}:${offeringId}`, null);
      dispatchCachedEvent(`${WB_BOOKING_CAPACITY_EVENT}:${offeringId}`, null);
      window.dispatchEvent(new CustomEvent(WB_BOOKING_REGISTER_EVENT, { detail: { offeringId, unregister: true } }));
    };
  }, [offeringId]);

  // Timezone for converting availableSlots UTC → display (offering tz or
  // event tz fallback). Defined ahead of the memos that bucket by day.
  const offeringTz = data?.offering.timezone || eventTz;

  const availableDates = useMemo(() => {
    if (!data) return [];
    // If treatments exist but none is selected, don't show any dates yet
    if (data.treatments.length > 0 && !selectedTreatment) return [];
    // Build set of lanes compatible with the selected treatment
    const compatibleLaneIds = new Set<string>();
    if (selectedTreatment) {
      for (const lane of data.lanes) {
        if (allowedLaneIds && !allowedLaneIds.has(lane.laneId)) continue;
        if (!lane.treatmentIds?.length || lane.treatmentIds.includes(selectedTreatment)) {
          compatibleLaneIds.add(lane.laneId);
        }
      }
    }
    const dates = new Set<string>();
    for (const slot of data.availableSlots) {
      // availableSlots now includes exhausted (spotsLeft: 0) slots for
      // the calendar's "Full" state — never offer them in the picker.
      if (slot.spotsLeft <= 0) continue;
      if (allowedLaneIds && !allowedLaneIds.has(slot.laneId)) continue;
      if (selectedTreatment && !compatibleLaneIds.has(slot.laneId)) continue;
      // Offering-zone calendar day (audit 2026-09-23, 4d).
      dates.add(dateKeyOf(slot.startDt, offeringTz));
    }
    return Array.from(dates).sort();
  }, [data, allowedLaneIds, selectedTreatment, offeringTz]);

  const filteredSlots = useMemo(() => {
    if (!data || !selectedDate) return [];
    // If treatments exist but none is selected, no slots should appear
    if (data.treatments.length > 0 && !selectedTreatment) return [];
    const compatibleLaneIds = new Set<string>();
    if (selectedTreatment) {
      for (const lane of data.lanes) {
        if (allowedLaneIds && !allowedLaneIds.has(lane.laneId)) continue;
        if (!lane.treatmentIds?.length || lane.treatmentIds.includes(selectedTreatment)) {
          compatibleLaneIds.add(lane.laneId);
        }
      }
    } else {
      // No treatments at all — allow all lanes
      for (const lane of data.lanes) {
        if (allowedLaneIds && !allowedLaneIds.has(lane.laneId)) continue;
        compatibleLaneIds.add(lane.laneId);
      }
    }
    return data.availableSlots.filter(
      (s) =>
        s.spotsLeft > 0 &&
        dateKeyOf(s.startDt, offeringTz) === selectedDate &&
        compatibleLaneIds.has(s.laneId),
    );
  }, [selectedDate, selectedTreatment, data, allowedLaneIds, offeringTz]);

  // Treatments filtered by visibility rules (treatment filters)
  const visibleTreatments = useMemo(() => {
    if (!data) return [];
    if (!allowedTreatmentIds) return data.treatments;
    return data.treatments.filter((tr) => allowedTreatmentIds.has(tr.treatmentId));
  }, [data, allowedTreatmentIds]);

  // Clear selection if the selected treatment gets filtered out
  useEffect(() => {
    if (selectedTreatment && !visibleTreatments.some((tr) => tr.treatmentId === selectedTreatment)) {
      setSelectedTreatment("");
      setSelectedDate("");
      setSelectedSlot(null);
    }
  }, [visibleTreatments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute available dates + time ranges from lane availability windows (for REQUEST_BASED dropdowns).
  // Uses laneAvailabilities (raw non-blocked, future windows) rather than availableSlots
  // (capacity-filtered computed slots) so REQUEST_BASED works even when all slots are booked.
  const requestDateInfo = useMemo(() => {
    const empty = { dates: [] as string[], timeRanges: new Map<string, { earliest: number; latest: number }>() };
    const windows = data?.laneAvailabilities;
    if (!windows?.length) return empty;
    // Same zone as the AUTO_BOOKABLE day list (L-3): offering, else event.
    const tz = offeringTz;
    const dateMap = new Map<string, { earliest: number; latest: number }>();
    const updateRange = (date: string, startMin: number, endMin: number) => {
      const existing = dateMap.get(date);
      if (existing) {
        existing.earliest = Math.min(existing.earliest, startMin);
        existing.latest = Math.max(existing.latest, endMin);
      } else {
        dateMap.set(date, { earliest: startMin, latest: endMin });
      }
    };
    for (const w of windows) {
      const start = utcToTzParts(w.startDt, tz);
      const end = utcToTzParts(w.endDt, tz);
      const startMin = parseHHMM(start.time);
      const endMin = parseHHMM(end.time);
      if (start.date === end.date) {
        updateRange(start.date, startMin, endMin);
      } else {
        updateRange(start.date, startMin, 23 * 60 + 45);
        updateRange(end.date, 0, endMin);
      }
    }
    return { dates: Array.from(dateMap.keys()).sort(), timeRanges: dateMap };
  }, [data, offeringTz]);

  // Shortest treatment duration → minimum window length for constrained selectors
  const minTreatmentDuration = useMemo(() => {
    if (!data?.treatments.length) return 30;
    return Math.min(...data.treatments.map((tr) => tr.durationMinutes ?? 30));
  }, [data]);

  // Props are primary source (always available from serialized JSON), API is secondary
  const resolvedBookingMode = bookingMode ?? data?.offering.bookingMode;
  const isRequestBased = resolvedBookingMode === "REQUEST_BASED";

  // Phase 7b: dispatch booking capacity when selection changes (namespaced by offeringId)
  useEffect(() => {
    if (!data || !offeringId) return;
    if (!isRequestBased && selectedSlot) {
      const treatment = data.treatments.find(tr => tr.treatmentId === selectedTreatment);
      const capacity = treatment?.capacity ?? 1;
      dispatchCachedEvent(`${WB_BOOKING_CAPACITY_EVENT}:${offeringId}`, {
        offeringId, treatmentCapacity: capacity, spotsLeft: selectedSlot.spotsLeft,
      });
    } else if (isRequestBased && selectedTreatmentIds.size > 0) {
      const minCapacity = Math.min(
        ...data.treatments
          .filter(tr => selectedTreatmentIds.has(tr.treatmentId))
          .map(tr => tr.capacity ?? 1)
      );
      dispatchCachedEvent(`${WB_BOOKING_CAPACITY_EVENT}:${offeringId}`, {
        offeringId, treatmentCapacity: minCapacity, spotsLeft: Infinity,
      });
    } else {
      // No selection — remove this offering's capacity entry
      dispatchCachedEvent(`${WB_BOOKING_CAPACITY_EVENT}:${offeringId}`, null);
    }
  }, [selectedSlot, selectedTreatment, selectedTreatmentIds, data, isRequestBased, offeringId]);

  // Broadcast booking summary so OverviewGroup can display it (namespaced by offeringId)
  const summaryEventKey = offeringId ? `${WB_BOOKING_SUMMARY_EVENT}:${offeringId}` : undefined;
  useEffect(() => {
    if (!summaryEventKey) return; // No offeringId configured — nothing to broadcast
    if (!data) {
      // Degraded REQUEST_BASED: we can still report window count
      if (isRequestBased) {
        const validCount = availabilityWindows.filter(w => w.date && w.startTime && w.endTime).length;
        if (selectedTreatmentIds.size > 0 || validCount > 0) {
          const validWindows = availabilityWindows
            .filter(w => w.date && w.startTime && w.endTime)
            .map(w => ({ date: w.date, startTime: w.startTime, endTime: w.endTime }));
          dispatchCachedEvent(summaryEventKey, {
            offeringId,
            mode: "REQUEST_BASED",
            treatmentCount: selectedTreatmentIds.size,
            windowCount: validCount,
            treatmentNames: [],
            availabilityWindows: validWindows,
          });
        } else {
          dispatchCachedEvent(summaryEventKey, null);
        }
      } else {
        dispatchCachedEvent(summaryEventKey, null);
      }
      return;
    }
    if (!isRequestBased && selectedSlot) {
      const treatmentName = data.treatments.find(tr => tr.treatmentId === selectedTreatment)?.name;
      const startDate = new Date(selectedSlot.startDt);
      const endDate = new Date(selectedSlot.endDt);
      const dateStr = startDate.toLocaleDateString(undefined, { timeZone: offeringTz, weekday: "short", month: "short", day: "numeric" });
      const timeStr = `${startDate.toLocaleTimeString(undefined, { timeZone: offeringTz, hour: "2-digit", minute: "2-digit" })} – ${endDate.toLocaleTimeString(undefined, { timeZone: offeringTz, hour: "2-digit", minute: "2-digit" })}`;
      dispatchCachedEvent(summaryEventKey, { offeringId, mode: "AUTO_BOOKABLE", treatmentName: treatmentName || "", dateTime: `${dateStr} · ${timeStr}` });
    } else if (isRequestBased && (selectedTreatmentIds.size > 0 || availabilityWindows.some(w => w.date && w.startTime && w.endTime))) {
      const validCount = availabilityWindows.filter(w => w.date && w.startTime && w.endTime).length;
      const treatmentNames = data.treatments
        .filter(tr => selectedTreatmentIds.has(tr.treatmentId))
        .map(tr => tr.name);
      const validWindows = availabilityWindows
        .filter(w => w.date && w.startTime && w.endTime)
        .map(w => ({ date: w.date, startTime: w.startTime, endTime: w.endTime }));
      dispatchCachedEvent(summaryEventKey, { offeringId, mode: "REQUEST_BASED", treatmentCount: selectedTreatmentIds.size, windowCount: validCount, treatmentNames, availabilityWindows: validWindows });
    } else {
      dispatchCachedEvent(summaryEventKey, null);
    }
  }, [selectedSlot, selectedTreatment, selectedTreatmentIds, availabilityWindows, data, isRequestBased, summaryEventKey, offeringId]);

  // Validation helper for REQUEST_BASED availability windows
  const validateWindows = useCallback((): { errors: (string | null)[]; hasError: boolean } => {
    const now = new Date();
    const errors: (string | null)[] = availabilityWindows.map((w) => {
      if (!w.date && !w.startTime && !w.endTime) return null;
      if (!w.date || !w.startTime || !w.endTime) return t("bookingWindowDateRequired");
      if (w.endTime <= w.startTime) return t("bookingWindowEndBeforeStart");
      // The window is an OFFERING-zone wall time (the selects list offering
      // hours); parsing it bare would use the visitor's browser zone and put
      // a future window "in the past" for anyone east of the offering (fix
      // round 1, l2 F-4). Same conversion the payload below uses.
      const windowStart = new Date(tzToUtcIso(w.date, w.startTime, offeringTz));
      if (windowStart < now) return t("bookingWindowInPast");
      if (data?.offering.startDt && data?.offering.endDt) {
        // The window's `date` is a wall date in the offering zone, so the
        // bounds must be offering-zone days too (audit 2026-09-23, 4d).
        const offeringStart = dateKeyOf(data.offering.startDt, offeringTz);
        const offeringEnd = dateKeyOf(data.offering.endDt, offeringTz);
        if (offeringStart && offeringEnd && (w.date < offeringStart || w.date > offeringEnd)) {
          return t("bookingWindowOutsideRange");
        }
      }
      return null;
    });
    return { errors, hasError: errors.some((e) => e !== null) };
  }, [availabilityWindows, data, offeringTz]);

  // Listen for DataInput validation + booking error events
  useEffect(() => {
    const form = containerRef.current?.closest("form");
    if (!form) return undefined;

    const handleValidate = (e: Event) => {
      // Clear stale booking error from a prior submission attempt
      setBookingError(null);

      // Same hidden-step contract as the form fields: a submit pass validates
      // skipped steps too (see formInput's `shouldValidateField`).
      const detail = (e as CustomEvent).detail as
        | {
            errorCount: number
            includeHidden?: boolean
            hiddenErrorNodes?: Element[]
          }
        | undefined;
      const includeHidden = detail?.includeHidden === true;
      if (!shouldValidateField(containerRef.current, includeHidden)) return;
      if (!resolvedBookingMode) return;

      // `reportValidationError` bumps the count AND reports the node when this
      // group sits on a skipped step. Both halves are required:
      // `decideValidationOutcome` compares errorCount against how many errors
      // were reported hidden, so a bare increment returns `{pass:false}` with
      // NO `jumpToStep` — a dead submit button whose message is on a
      // `display:none` step, with nothing on screen to explain it.
      //
      // Called once PER ERROR, not per pass: the request branch can raise two
      // in one go and the gate's arithmetic has to stay in step.
      if (isRequestBased) {
        const { errors, hasError } = validateWindows();
        setWindowErrors(errors);
        if (hasError) reportValidationError(detail, containerRef.current);
        if (treatmentRequired !== false && data?.treatments && selectedTreatmentIds.size === 0) {
          setTreatmentError(t("bookingTreatmentSelectionRequired"));
          reportValidationError(detail, containerRef.current);
        }
      } else {
        // AUTO_BOOKABLE: require a slot to be selected
        if (!selectedSlot) {
          setBookingError(t("bookingNoAppointment"));
          reportValidationError(detail, containerRef.current);
        }
      }
    };

    const handleBookingError = (e: Event) => {
      const detail = (e as CustomEvent).detail as { offeringId?: string; message?: string } | undefined;
      // Only show error if it's for this BookingGroup's offering
      if (detail?.offeringId && detail.offeringId !== offeringId) return;
      setBookingError(detail?.message || t("bookingSubmissionFailed"));
    };

    form.addEventListener(WB_VALIDATE_EVENT, handleValidate);
    form.addEventListener(WB_BOOKING_ERROR_EVENT, handleBookingError);
    return () => {
      form.removeEventListener(WB_VALIDATE_EVENT, handleValidate);
      form.removeEventListener(WB_BOOKING_ERROR_EVENT, handleBookingError);
    };
  }, [resolvedBookingMode, isRequestBased, validateWindows, selectedTreatmentIds, data, treatmentRequired, selectedSlot]);

  // Phase 5: Refresh slots when modal opens (AUTO_BOOKABLE staleness detection)
  useEffect(() => {
    if (!modalOpen || !offeringId || !data || isRequestBased) return;
    let active = true;

    fetchBookingPage(offeringId)
      .then((fresh) => {
        if (!active) return;
        setData(fresh);
        if (selectedSlot) {
          const stillExists = fresh.availableSlots.some(
            (s) => s.spotsLeft > 0 && s.laneId === selectedSlot.laneId && s.startDt === selectedSlot.startDt,
          );
          if (!stillExists) setSelectedSlot(null);
        }
      })
      .catch(() => { /* keep stale data */ });

    return () => { active = false; };
  }, [modalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Visibility gate — after all hooks
  if (isVisible === false) return null;

  const validWindows = availabilityWindows.filter((w) => w.date && w.startTime && w.endTime);

  // Container styling from props
  const containerStyle: React.CSSProperties = {};
  if (backgroundColor) containerStyle.backgroundColor = backgroundColor;
  if (padding) containerStyle.padding = parsePaddingPx(padding);
  if (border) {
    const bw = borderWidth ? parseInt(borderWidth) + "px" : "1px";
    const bs = borderStyle || "solid";
    const bc = borderColor || "var(--wb-secondary, #e2e8f0)";
    if (borderSides?.length) {
      if (borderSides.includes("top")) containerStyle.borderTop = `${bw} ${bs} ${bc}`;
      if (borderSides.includes("right")) containerStyle.borderRight = `${bw} ${bs} ${bc}`;
      if (borderSides.includes("bottom")) containerStyle.borderBottom = `${bw} ${bs} ${bc}`;
      if (borderSides.includes("left")) containerStyle.borderLeft = `${bw} ${bs} ${bc}`;
    } else {
      containerStyle.border = `${bw} ${bs} ${bc}`;
    }
  }
  if (borderRadius) containerStyle.borderRadius = parseBorderRadiusPxLocal(borderRadius) + "px";

  const selectedBorder = selectionActiveBorderColor
    ? `2px solid ${selectionActiveBorderColor}`
    : "2px solid var(--wb-secondary, #2563eb)";
  const defaultBorder = selectionBorderColor
    ? `1px solid ${selectionBorderColor}`
    : "1px solid color-mix(in srgb, var(--wb-secondary, #e2e8f0) 30%, transparent)";
  const selectedBg = selectionActiveBackgroundColor
    || "color-mix(in srgb, var(--wb-secondary, #2563eb) 5%, transparent)";
  const rounding = summaryCardBorderRadius ? parseBorderRadiusPxLocal(summaryCardBorderRadius) + "px" : "8px";
  const mutedColor = "var(--wb-primary, #64748b)";
  const secondaryColor = "var(--wb-secondary, #2563eb)";
  const resolvedEditColor = editButtonColor || secondaryColor;
  const resolvedEditLabel = editButtonLabel || t("bookingEdit");
  const summaryBorder = summaryCardBorderColor
    ? `1px solid ${summaryCardBorderColor}`
    : defaultBorder;
  const resolvedCancelBorderColor = cancelButtonBorderColor || (secondaryColor + "40");
  const resolvedCancelTextColor = cancelButtonTextColor || secondaryColor;
  const resolvedConfirmBg = confirmButtonBackgroundColor || "var(--wb-primary, #121212)";
  const resolvedConfirmTextColor = confirmButtonTextColor || "var(--wb-background, #fff)";
  const resolvedCancelBgHover = cancelButtonBgHoverColor || (cancelButtonBackgroundColor === "transparent" ? (secondaryColor + "08") : cancelButtonBackgroundColor || "transparent");
  const resolvedCancelTextHover = cancelButtonTextHoverColor || resolvedCancelTextColor;
  const resolvedCancelBorderHover = cancelButtonBorderHoverColor || resolvedCancelBorderColor;
  const resolvedConfirmBgHover = confirmButtonBgHoverColor || "color-mix(in srgb, var(--wb-primary, #121212) 87%, transparent)";
  const resolvedConfirmTextHover = confirmButtonTextHoverColor || resolvedConfirmTextColor;
  const resolvedAvailBorderColor = availabilityInputBorderColor || (secondaryColor + "30");
  const resolvedAvailBgColor = availabilityInputBackgroundColor || "transparent";
  const resolvedAvailTextColor = availabilityInputTextColor || "var(--wb-primary, #121212)";
  const resolvedAvailBorderRadius = availabilityInputBorderRadius ? parseBorderRadiusPxLocal(availabilityInputBorderRadius) + "px" : rounding;
  const modalStyleOverrides = {
    modalBackgroundColor,
    modalBorderRadius: modalBorderRadius ? parseBorderRadiusPxLocal(modalBorderRadius) : undefined,
    modalMaxWidth: modalMaxWidth ? parseInt(modalMaxWidth) : undefined,
    modalTitleColor,
    modalTitleFontSize: modalTitleFontSize ? tailwindFontSizeToCssLocal(modalTitleFontSize) : undefined,
    modalFooterBorderColor,
    modalBackdropOpacity,
    closeIconColor,
    modalBorderColor,
  };

  const treatmentName = selectedTreatment
    ? data?.treatments.find((tr) => tr.treatmentId === selectedTreatment)?.name
    : undefined;

  const hasSelection = isRequestBased
    ? selectedTreatmentIds.size > 0 || validWindows.length > 0
    : Boolean(selectedSlot);

  const resolvedModalTitle = offeringName || data?.offering.name || t("bookingBook");

  // Modal content for REQUEST_BASED mode
  const requestBasedModalContent = (
    <>
      {data?.offering.description && (
        <p style={{ color: mutedColor, opacity: 0.6, fontSize: "0.875rem", margin: 0 }}>
          {data.offering.description}
        </p>
      )}

      {/* Treatment multi-select checkboxes */}
      <div>
        <h4 style={{ fontSize: "0.875rem", fontWeight: 500, marginBottom: 8 }}>{treatmentsLabel || t("slottingSelectTreatments")}</h4>
        {treatmentError && (
          <span style={{ fontSize: "0.75rem", color: "#ef4444", display: "block", marginBottom: 8 }}>
            {treatmentError}
          </span>
        )}
        {data?.treatments ? (
          visibleTreatments.length === 0 ? (
            <p style={{ color: mutedColor, opacity: 0.6, fontSize: "0.875rem", margin: 0 }}>
              {t("bookingTreatmentsUnavailable")}
            </p>
          ) : (
          <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
            {visibleTreatments.map((tr) => {
              const checked = selectedTreatmentIds.has(tr.treatmentId);
              return (
                <button
                  type="button"
                  key={tr.treatmentId}
                  onClick={() => {
                    setSelectedTreatmentIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(tr.treatmentId)) next.delete(tr.treatmentId);
                      else next.add(tr.treatmentId);
                      return next;
                    });
                    setTreatmentError(null);
                  }}
                  style={{
                    textAlign: "left", padding: 12, borderRadius: rounding,
                    border: checked ? selectedBorder : defaultBorder,
                    background: checked ? selectedBg : "transparent",
                    cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
                  }}
                >
                  <span style={{
                    width: 16, height: 16, borderRadius: 3, flexShrink: 0,
                    border: checked ? `2px solid ${secondaryColor}` : `2px solid color-mix(in srgb, ${secondaryColor} 40%, transparent)`,
                    background: checked ? secondaryColor : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "var(--wb-background, #fff)", fontSize: 11,
                  }}>
                    {checked ? "\u2713" : ""}
                  </span>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: "0.875rem" }}>{tr.name}</div>
                    <div style={{ fontSize: "0.75rem", color: mutedColor, opacity: 0.6, marginTop: 2 }}>{tr.durationMinutes} min</div>
                  </div>
                </button>
              );
            })}
          </div>
          )
        ) : (
          <p style={{ color: mutedColor, opacity: 0.6, fontSize: "0.875rem", margin: 0 }}>
            {t("bookingTreatmentsUnavailable")}
          </p>
        )}
      </div>

      {/* Availability window builder — only rendered when slot data exists */}
      {requestDateInfo.dates.length > 0 ? (
      <div>
        <h4 style={{ fontSize: "0.875rem", fontWeight: 500, marginBottom: 8 }}>{availabilityLabel || t("slottingYourAvailability")}</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {availabilityWindows.map((w, i) => {
            const range = w.date ? requestDateInfo.timeRanges.get(w.date) : undefined;
            const startOptions = range ? generateTimeOptions(range.earliest, range.latest - minTreatmentDuration) : [];
            const endOptions = range && w.startTime ? generateTimeOptions(parseHHMM(w.startTime) + minTreatmentDuration, range.latest) : [];
            const clearWindowError = () => setWindowErrors((prev) => { const copy = [...prev]; copy[i] = null; return copy; });
            const selectStyle: React.CSSProperties = { padding: "6px 10px", borderRadius: resolvedAvailBorderRadius, border: `1px solid ${resolvedAvailBorderColor}`, backgroundColor: resolvedAvailBgColor, color: resolvedAvailTextColor, fontSize: "0.875rem", outline: "none", cursor: "pointer", minWidth: 0 };
            const removeBtn = availabilityWindows.length > 1 ? (
              <button type="button" onClick={() => { setAvailabilityWindows(availabilityWindows.filter((_, j) => j !== i)); setWindowErrors((prev) => prev.filter((_, j) => j !== i)); }}
                style={{ padding: "2px 6px", color: mutedColor, background: "none", border: "none", cursor: "pointer", fontSize: "1rem" }}>&times;</button>
            ) : null;
            const errorEl = windowErrors[i] ? <span style={{ fontSize: "0.75rem", color: "#ef4444", marginTop: 2 }}>{windowErrors[i]}</span> : null;

            // Constrained mode: dropdowns derived from availableSlots
            return (
              <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  {/* Date dropdown */}
                  <select value={w.date}
                    onChange={(e) => {
                      const newDate = e.target.value;
                      const next = [...availabilityWindows];
                      const r = requestDateInfo.timeRanges.get(newDate);
                      if (r) {
                        const startMin = Math.ceil(r.earliest / 15) * 15;
                        const endMin = Math.min(startMin + minTreatmentDuration, r.latest);
                        next[i] = { date: newDate, startTime: formatMinutesHHMM(startMin), endTime: formatMinutesHHMM(endMin) };
                      } else {
                        next[i] = { date: newDate, startTime: "", endTime: "" };
                      }
                      setAvailabilityWindows(next);
                      clearWindowError();
                    }}
                    style={selectStyle}>
                    <option value="">{t("bookingSelectDate")}</option>
                    {requestDateInfo.dates.map((d) => (
                      <option key={d} value={d}>
                        {/* `d` is a bare offering-zone date: render it unshifted
                            (local parse + foreign timeZone was the M24 shape; the
                            noon trick only survives offsets under 12 h). */}
                        {formatDateOnly(d, undefined, { weekday: "short", month: "short", day: "numeric" })}
                      </option>
                    ))}
                  </select>

                  {/* Start time dropdown */}
                  <span style={{ fontSize: "0.75rem", color: mutedColor }}>{t("slottingStartTime")}</span>
                  <select value={w.startTime} disabled={!w.date}
                    onChange={(e) => {
                      const newStart = e.target.value;
                      const next = [...availabilityWindows];
                      const newStartMin = parseHHMM(newStart);
                      const currentEndMin = w.endTime ? parseHHMM(w.endTime) : 0;
                      if (!w.endTime || currentEndMin < newStartMin + minTreatmentDuration) {
                        const r = requestDateInfo.timeRanges.get(w.date);
                        const newEndMin = Math.min(newStartMin + minTreatmentDuration, r?.latest ?? newStartMin + minTreatmentDuration);
                        next[i] = { ...w, startTime: newStart, endTime: formatMinutesHHMM(newEndMin) };
                      } else {
                        next[i] = { ...w, startTime: newStart };
                      }
                      setAvailabilityWindows(next);
                      clearWindowError();
                    }}
                    style={{ ...selectStyle, opacity: w.date ? 1 : 0.5 }}>
                    <option value="">{t("bookingSelectTime")}</option>
                    {startOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>

                  {/* End time dropdown */}
                  <span style={{ fontSize: "0.75rem", color: mutedColor }}>{t("slottingEndTime")}</span>
                  <select value={w.endTime} disabled={!w.startTime}
                    onChange={(e) => {
                      const next = [...availabilityWindows];
                      next[i] = { ...w, endTime: e.target.value };
                      setAvailabilityWindows(next);
                      clearWindowError();
                    }}
                    style={{ ...selectStyle, opacity: w.startTime ? 1 : 0.5 }}>
                    <option value="">{t("bookingSelectTime")}</option>
                    {endOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>

                  {removeBtn}
                </div>
                {errorEl}
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => setAvailabilityWindows([...availabilityWindows, { date: "", startTime: "", endTime: "" }])}
            style={{ fontSize: "0.8125rem", color: secondaryColor, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textAlign: "left", padding: 0 }}
          >
            + {addWindowLabel || t("slottingAddWindow")}
          </button>
        </div>
      </div>
      ) : (
        <p style={{ color: mutedColor, opacity: 0.6, fontSize: "0.875rem", margin: 0 }}>
          {t("slottingNoAvailableDates")}
        </p>
      )}
    </>
  );

  // Modal content for AUTO_BOOKABLE mode
  const autoBookableModalContent = data ? (
    <>
      {data.offering.description && (
        <p style={{ color: mutedColor, opacity: 0.6, fontSize: "0.875rem", margin: 0 }}>
          {data.offering.description}
        </p>
      )}

      {/* Treatment selector */}
      {visibleTreatments.length === 0 && data.treatments.length > 0 && (
        <p style={{ color: mutedColor, opacity: 0.6, fontSize: "0.875rem", margin: 0 }}>
          {t("bookingTreatmentsUnavailable")}
        </p>
      )}
      {visibleTreatments.length >= 1 && (
        <div>
          <h4 style={{ fontSize: "0.875rem", fontWeight: 500, marginBottom: 8 }}>{t("slottingSelectService")}</h4>
          <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
            {visibleTreatments.map((tr) => (
              <button
                type="button"
                key={tr.treatmentId}
                onClick={() => { setSelectedTreatment(tr.treatmentId); setSelectedDate(""); setSelectedSlot(null); setModalValidationError(null); }}
                style={{
                  textAlign: "left", padding: 12, borderRadius: rounding,
                  border: selectedTreatment === tr.treatmentId ? selectedBorder : defaultBorder,
                  background: selectedTreatment === tr.treatmentId ? selectedBg : "transparent",
                  cursor: "pointer",
                }}
              >
                <div style={{ fontWeight: 500, fontSize: "0.875rem" }}>{tr.name}</div>
                <div style={{ fontSize: "0.75rem", color: mutedColor, opacity: 0.6, marginTop: 2 }}>{tr.durationMinutes} min</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Date picker — hidden when treatments exist but all are filtered out */}
      {(visibleTreatments.length > 0 || data.treatments.length === 0) && <div>
        <h4 style={{ fontSize: "0.875rem", fontWeight: 500, marginBottom: 8 }}>{selectDateLabel || t("slottingSelectDate")}</h4>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {availableDates.length === 0 ? (
            <p style={{ color: mutedColor, opacity: 0.6, fontSize: "0.875rem" }}>{t("slottingNoAvailableDates")}</p>
          ) : (
            availableDates.map((date) => (
              <button
                type="button"
                key={date}
                onClick={() => { setSelectedDate(date); setSelectedSlot(null); setModalValidationError(null); }}
                style={{
                  padding: "6px 12px", borderRadius: rounding, fontSize: "0.875rem", cursor: "pointer",
                  border: selectedDate === date ? selectedBorder : defaultBorder,
                  background: selectedDate === date ? selectedBg : "transparent",
                }}
              >
                {/* M24 REGRESSION FIX: `new Date(date + "T00:00:00")` is LOCAL
                    midnight; formatting it in the OFFERING timezone shifts the
                    calendar date backwards on any browser east of UTC, so the
                    chip showed the PREVIOUS day while the slot times under it
                    were right. A date-only value has no time-of-day and must
                    not be moved across zones — formatDateOnly parses as UTC
                    midnight and formats in UTC. Fixed once under BD-6 for the
                    old slotting widget; that widget was retired and this
                    replacement reintroduced the pattern, with the original pin
                    retired alongside it. */}
                {formatDateOnly(date, undefined, { weekday: "short", month: "short", day: "numeric" })}
              </button>
            ))
          )}
        </div>
      </div>}

      {/* Time slots — hidden when treatments exist but all are filtered out */}
      {(visibleTreatments.length > 0 || data.treatments.length === 0) && selectedDate && (
        <div>
          <h4 style={{ fontSize: "0.875rem", fontWeight: 500, marginBottom: 8 }}>{availableTimesLabel || t("slottingAvailableTimes")}</h4>
          {filteredSlots.length === 0 ? (
            <p style={{ color: mutedColor, opacity: 0.6, fontSize: "0.875rem" }}>{t("slottingNoAvailableTimes")}</p>
          ) : (
            <div style={{ display: "grid", gap: 6, gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))" }}>
              {filteredSlots.map((slot, i) => {
                const start = new Date(slot.startDt).toLocaleTimeString(undefined, { timeZone: offeringTz, hour: "2-digit", minute: "2-digit" });
                const end = new Date(slot.endDt).toLocaleTimeString(undefined, { timeZone: offeringTz, hour: "2-digit", minute: "2-digit" });
                const isSelected = selectedSlot?.startDt === slot.startDt && selectedSlot?.laneId === slot.laneId;
                return (
                  <button
                    type="button"
                    key={`${slot.laneId}-${slot.startDt}-${i}`}
                    onClick={() => { setSelectedSlot(slot); setModalValidationError(null); }}
                    style={{
                      padding: 10, borderRadius: rounding, textAlign: "left", fontSize: "0.875rem", cursor: "pointer",
                      border: isSelected ? selectedBorder : defaultBorder,
                      background: isSelected ? selectedBg : "transparent",
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>{start} &ndash; {end}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </>
  ) : (
    // Degraded: no API data for AUTO_BOOKABLE
    <div style={{ textAlign: "center", padding: "24px 0", color: mutedColor, opacity: 0.6, fontSize: "0.875rem" }}>
      {apiStatus === "access_denied" ? t("bookingAccessDenied") : error || t("slottingBookingUnavailable")}
    </div>
  );

  return (
    <div ref={containerRef} style={{ ...containerStyle, display: "flex", flexDirection: "column", gap: resolveFieldSpacing(fieldSpacing) }}>
      {/* Group label */}
      {groupLabel && (
        <span style={{ fontWeight: 500, fontSize: "0.875rem", color: groupLabelColor || undefined }}>
          {groupLabel}
        </span>
      )}

      {/* Loading spinner */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
          <div style={{ width: 20, height: 20, border: "2px solid var(--wb-secondary, #ddd)", borderTopColor: "var(--wb-primary, #121212)", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
        </div>
      ) : !resolvedBookingMode ? (
        // No booking mode from props or API — show unavailable
        !offeringId ? (
          // No token — placeholder summary card
          <div
            style={{
              border: `1px solid color-mix(in srgb, var(--wb-secondary, #e2e8f0) 30%, transparent)`,
              borderRadius: rounding,
              padding: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: "0.875rem", color: "var(--wb-primary, #64748b)", opacity: 0.6 }}>
                {offeringName ? `${offeringName} — ${t("bookingNoAppointment")}` : t("bookingNoAppointment")}
              </span>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "24px 0", color: "var(--wb-primary, #64748b)", opacity: 0.6, fontSize: "0.875rem" }}>
            {error || t("slottingBookingUnavailable")}
          </div>
        )
      ) : (
        <>
          {/* Hidden form fields — namespaced by offeringId for multi-BookingGroup support */}
          <input type="hidden" name={`__booking_${offeringId}_offeringId`} value={offeringId ?? ""} />
          {isRequestBased ? (
            <>
              <input type="hidden" name={`__booking_${offeringId}_mode`} value="REQUEST_BASED" />
              <input type="hidden" name={`__booking_${offeringId}_treatmentIds`} value={JSON.stringify([...selectedTreatmentIds])} />
              <input type="hidden" name={`__booking_${offeringId}_availability`} value={JSON.stringify(validWindows.map((w) => ({
                startDt: requestDateInfo.dates.length > 0 ? tzToUtcIso(w.date, w.startTime, offeringTz) : `${w.date}T${w.startTime}:00Z`,
                endDt: requestDateInfo.dates.length > 0 ? tzToUtcIso(w.date, w.endTime, offeringTz) : `${w.date}T${w.endTime}:00Z`,
              })))} />
            </>
          ) : (
            <>
              <input type="hidden" name={`__booking_${offeringId}_treatmentId`} value={selectedTreatment} />
              <input type="hidden" name={`__booking_${offeringId}_laneId`} value={selectedSlot?.laneId ?? ""} />
              <input type="hidden" name={`__booking_${offeringId}_startDt`} value={selectedSlot?.startDt ?? ""} />
              <input type="hidden" name={`__booking_${offeringId}_endDt`} value={selectedSlot?.endDt ?? ""} />
            </>
          )}

          {/* Inline summary */}
          <div
            style={{
              border: summaryBorder,
              borderRadius: rounding,
              padding: 12,
            }}
          >
            {isRequestBased ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: "0.875rem", color: hasSelection ? undefined : mutedColor, opacity: hasSelection ? 1 : 0.6 }}>
                  {hasSelection
                    ? t("slottingRequestSummary", String(selectedTreatmentIds.size), String(validWindows.length))
                    : t("slottingNoRequestConfigured")}
                </span>
                <button
                  type="button"
                  onClick={() => setModalOpen(true)}
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: resolvedEditColor,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    padding: "0.25rem 0.5rem",
                    textDecoration: "underline",
                    textUnderlineOffset: "2px",
                    flexShrink: 0,
                  }}
                >
                  {resolvedEditLabel}
                </button>
              </div>
            ) : hasSelection && selectedSlot ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                  {treatmentName ? (
                    <span style={{ fontWeight: 500, fontSize: "0.875rem" }}>{treatmentName}</span>
                  ) : null}
                  <span style={{ fontSize: "0.8125rem", color: mutedColor }}>
                    {new Date(selectedSlot.startDt).toLocaleDateString(undefined, { timeZone: offeringTz, weekday: "short", month: "short", day: "numeric" })}
                    {" · "}
                    {new Date(selectedSlot.startDt).toLocaleTimeString(undefined, { timeZone: offeringTz, hour: "2-digit", minute: "2-digit" })}
                    {" – "}
                    {new Date(selectedSlot.endDt).toLocaleTimeString(undefined, { timeZone: offeringTz, hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setModalOpen(true)}
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: resolvedEditColor,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    padding: "0.25rem 0.5rem",
                    textDecoration: "underline",
                    textUnderlineOffset: "2px",
                    flexShrink: 0,
                  }}
                >
                  {resolvedEditLabel}
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: "0.875rem", color: mutedColor, opacity: 0.6 }}>
                  {offeringName ? `${offeringName} — ${t("bookingNoAppointment")}` : t("bookingNoAppointment")}
                </span>
                <button
                  type="button"
                  onClick={() => setModalOpen(true)}
                  style={{
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    padding: "0.375rem 0.75rem",
                    borderRadius: rounding,
                    border: `1px solid ${secondaryColor}`,
                    color: secondaryColor,
                    background: "transparent",
                    cursor: "pointer",
                  }}
                >
                  {t("bookingBook")}
                </button>
              </div>
            )}
          </div>

          {/* Booking error feedback */}
          {bookingError && (
            <span style={{ fontSize: "0.75rem", color: "#ef4444", marginTop: -4 }}>
              {bookingError}
            </span>
          )}

          {/* Booking flow modal */}
          <FormModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            title={resolvedModalTitle}
            styleOverrides={modalStyleOverrides}
            footer={
              <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
                {modalValidationError && (
                  <p style={{ fontSize: "0.75rem", fontWeight: 500, color: "#ef4444", margin: 0 }}>
                    {modalValidationError}
                  </p>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                {cancelButtonLabel ? (
                  <button
                    type="button"
                    onClick={() => { setModalValidationError(null); setModalOpen(false); }}
                    onMouseEnter={() => setCancelHovered(true)}
                    onMouseLeave={() => setCancelHovered(false)}
                    style={{
                      padding: "0.5rem 1rem",
                      border: `1px solid ${cancelHovered ? resolvedCancelBorderHover : resolvedCancelBorderColor}`,
                      borderRadius: cancelButtonBorderRadius ? parseInt(cancelButtonBorderRadius) : 8,
                      background: cancelHovered ? resolvedCancelBgHover : (cancelButtonBackgroundColor || "transparent"),
                      color: cancelHovered ? resolvedCancelTextHover : resolvedCancelTextColor,
                      cursor: "pointer",
                      fontSize: "0.875rem",
                      transition: "background 0.15s, color 0.15s, border-color 0.15s",
                    }}
                  >
                    {cancelButtonLabel}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    if (isRequestBased) {
                      let hasError = false;
                      if (treatmentRequired !== false && data?.treatments && selectedTreatmentIds.size === 0) {
                        setTreatmentError(t("bookingTreatmentSelectionRequired"));
                        hasError = true;
                      }
                      const { errors, hasError: windowHasError } = validateWindows();
                      setWindowErrors(errors);
                      if (windowHasError) hasError = true;
                      if (hasError) return;
                    } else {
                      // AUTO_BOOKABLE validation
                      if (visibleTreatments.length >= 1 && !selectedTreatment) {
                        setModalValidationError(t("bookingSelectTreatmentRequired"));
                        return;
                      }
                      if (!selectedDate) {
                        setModalValidationError(t("bookingSelectDateRequired"));
                        return;
                      }
                      if (!selectedSlot) {
                        setModalValidationError(t("bookingSelectSlotRequired"));
                        return;
                      }
                    }
                    setModalValidationError(null);
                    setTreatmentError(null);
                    setWindowErrors([]);
                    setBookingError(null);
                    setModalOpen(false);
                  }}
                  onMouseEnter={() => setConfirmHovered(true)}
                  onMouseLeave={() => setConfirmHovered(false)}
                  style={{
                    padding: "0.5rem 1rem",
                    border: "none",
                    borderRadius: confirmButtonBorderRadius ? parseInt(confirmButtonBorderRadius) : 8,
                    background: confirmHovered ? resolvedConfirmBgHover : resolvedConfirmBg,
                    color: confirmHovered ? resolvedConfirmTextHover : resolvedConfirmTextColor,
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    transition: "background 0.15s, color 0.15s",
                  }}
                >
                  {confirmButtonLabel || t("bookingConfirm")}
                </button>
                </div>
              </div>
            }
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {isRequestBased ? requestBasedModalContent : autoBookableModalContent}
            </div>
          </FormModal>
        </>
      )}
    </div>
  );
}
